import * as React from "react";
import { ProjectResource, ControlType } from "client/resources";
import { VariableType } from "client/resources/variableResource";
import { repository } from "clientInstance";
import { Omit } from "recompose";
import { DoBusyTask } from "components/DataBaseComponent/DataBaseComponent";
import { ReferenceCallback, VariableCallbacks, collateProjectVariables } from "utils/ProjectVariableCollation";

interface WithProjectVariablesExternalProps {
    projectId: string;
    doBusyTask: DoBusyTask;
}

interface WithProjectVariablesInjectedProps {
    variables: string[];
    isRefreshing: boolean;
    projectId: string;
    project?: ProjectResource;
    error?: string;
    onRequestRefresh: () => Promise<any>;
}

interface WithProjectVariablesState {
    variables: string[];
    isRefreshing: boolean;
    project: ProjectResource;
    error?: string;
    isDataLoaded: boolean;
}

interface Callbacks extends VariableCallbacks {
    fetchProject: ReferenceCallback<ProjectResource>;
}

interface WithProjectVariableOptions extends Partial<Callbacks> {
    controlType: ControlType;
    variableType: VariableType;
}

const withProjectVariables = ({
    controlType,
    variableType,
    fetchLibraryVariableSet= (id) => repository.LibraryVariableSets.get(id),
    fetchVariableSet=  (id) => repository.Variables.get(id),
    fetchProject= (id) => repository.Projects.get(id)
}: WithProjectVariableOptions) =>
    <TOriginalProps extends any>(
        Component: (React.ComponentType<TOriginalProps>)
    ) => {
        const callbacks: VariableCallbacks = { fetchLibraryVariableSet, fetchVariableSet };
        const fetchVariablesForProject = collateProjectVariables(callbacks, controlType, variableType);

        type ResultProps = Omit<TOriginalProps, keyof WithProjectVariablesInjectedProps> & WithProjectVariablesExternalProps;
        const result = class WithProjectVariables extends React.Component<ResultProps, WithProjectVariablesState> {
            static displayName = `WithProjectVariables(${Component.displayName || Component.name})`;

            constructor(props: ResultProps) {
                super(props);
                this.state = {
                    error: null,
                    isRefreshing: false,
                    project: null,
                    variables: [],
                    isDataLoaded: false
                };
            }

            async componentDidMount() {
                this.props.doBusyTask(async () => {
                    const project = await fetchProject(this.props.projectId);
                    const variables = await fetchVariablesForProject(project);

                    this.setState({project, variables, isDataLoaded: true});
                });
            }

            render(): JSX.Element {
                const { isDataLoaded, ...state } = this.state;
                return (<Component {...this.props} {...state} onRequestRefresh={this.onRequestRefresh} />);
            }

            private onRequestRefresh = async () => {
                this.setState({ isRefreshing: true });

                try {
                    const variables = await fetchVariablesForProject(this.state.project);
                    this.setState({variables});
                } finally {
                    this.setState({ isRefreshing: false });
                }
            }
        };

        return result;
    };

export default withProjectVariables;
export {
    withProjectVariables,
    WithProjectVariablesInjectedProps,
    WithProjectVariableOptions,
    WithProjectVariablesExternalProps,
    WithProjectVariablesState
};


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/form/withProjectVariables.tsx